import { Dispatch } from 'redux';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import _toNumber from 'lodash/toNumber';
import { FixMeLaterPowerBIReports, Project, UserComment } from 'types/project';
import { AxiosError } from 'axios';
// eslint-disable-next-line import/no-cycle
import { clearAllProjectData } from '../../common/engine/actions/clearAllProjectData';
import {
  getProjectById,
  getProjectSettingsById,
  updateProjectSettings,
  getUsersForComments,
  getProjectNavigationSettings,
  GetProjectNavigationSettingsResponse,
} from '../../common/services/projectService';
import { getReports } from '../analyses/powerBiService';
import featureFlags from '../../common/helpers/featureFlags';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FixMeLaterProjectSettings = any;

export const defaultCurrency = 'Dollar';
export const defaultFinancialsMagnitude = 'Millions';
export const defaultFinancialsDecimalCount = 1;
export const defaultProjectTheme = '0';
export const defaultColourConversion = 'Default';
export const customColourConversion = 'Custom';

const isColorConversionModalActive = (
  state: Project,
  ContentColourConversionOption: string
) => {
  // The below logic is to show color conversion modal only once (light mode scenario)
  // If the redux state value is not equal to the settings api response and the api is called more than once then modal can be shown
  if (
    state.settingsApiCount === 0 &&
    ContentColourConversionOption === 'Custom'
  ) {
    return false;
  }
  if (
    state.ContentColourConversionOption !== ContentColourConversionOption &&
    ContentColourConversionOption === 'Custom'
  ) {
    return true;
  }
  return false;
};
export const initialState: Project = {
  id: '',
  type: '',
  name: null,
  status: null,
  isLoading: false,
  isLoaded: false,
  hasError: false,
  showDiagnostic: true,
  errorStatusCode: null,
  powerBiReports: [],
  currency: defaultCurrency,
  financialsMagnitude: defaultFinancialsMagnitude,
  financialsDecimalCount: defaultFinancialsDecimalCount,
  serviceIds: [],
  EngagementUrl: '',
  sharePointSiteUrl: '',
  users: [],
  navigation: undefined,
  skipPowerBiTheme: false,
  usersForCommenting: [],
  DoNotCloneVisualization: 'false',
  ManualRefreshEnabled: 'false',
  DefaultTheme: defaultProjectTheme,
  usersForCommentingLoaded: false,
  enableTpal: false,
  enableTableauProject: false,
  WorkspaceId: undefined,
  ENABLE_DYNAMIC_INGESTION: true,
  StructuredV2Enabled: false,
  SourceProjectUrl: '',
  SourceEngagementId: '',
  industryId: '',
  restriction: '',
  enablePerformanceTest: null,
  enablePowerBiIngestion: false,
  ContentColourConversionOption: defaultColourConversion,
  ImageIngestionEnabled: 'true',
  settingsApiCount: 0,
  showColorConversionModal: false,
};

const getFeatureFlagValue = (v: string) => {
  if (typeof v === 'undefined' || v === null) {
    return false;
  }
  return v.toString().toLowerCase() === 'true';
};

const projectSlice = createSlice({
  name: 'project',
  initialState,
  reducers: {
    resetProject: () => initialState,
    resetLoadedStatus: (state) => ({ ...state, isLoaded: false }),
    fetchProjectBegan: (state) => ({
      ...state,
      isLoading: true,
      hasError: false,
      errorStatusCode: null,
      status: null,
    }),
    fetchProjectEnded: (state) => ({
      ...state,
      isLoading: false,
      isLoaded: true,
    }),
    fetchProjectSettingsSucceeded: (
      state,
      action: {
        payload: {
          currency: string;
          financialsMagnitude: string;
          financialsDecimalCount: number;
          SkipPowerBiTheme: 'false' | 'true';
          enableTpal: 'false' | 'true';
          ENABLE_TABLEAU_PROJECT: 'false' | 'true';
          ENABLE_DYNAMIC_INGESTION: 'false' | 'true';
          StructuredV2Enabled: string;
          SharepointPerformanceTestEnabled: 'false' | 'true';
          enablePowerBiIngestion: 'false' | 'true';
          ImageIngestionEnabled: string;
          ContentColourConversionOption: string;
        };
      }
    ) => {
      const {
        currency,
        financialsMagnitude,
        financialsDecimalCount,
        SkipPowerBiTheme = 'false',
        enableTpal = 'true',
        ENABLE_TABLEAU_PROJECT = 'true',
        ENABLE_DYNAMIC_INGESTION,
        StructuredV2Enabled,
        SharepointPerformanceTestEnabled = 'false',
        enablePowerBiIngestion = 'true',
        ImageIngestionEnabled = 'true',
        ContentColourConversionOption,
      } = action.payload;

      const enableDynamicIngestion = () => {
        if (typeof ENABLE_DYNAMIC_INGESTION === 'undefined') {
          return true;
        }
        if (ENABLE_DYNAMIC_INGESTION === null) {
          return false;
        }
        return ENABLE_DYNAMIC_INGESTION.toString().toLowerCase() === 'true';
      };

      const financialsDecimalCountToUse = _toNumber(
        financialsDecimalCount || defaultFinancialsDecimalCount
      );

      return {
        ...state,
        ...action.payload,
        currency: currency || defaultCurrency,
        financialsMagnitude: financialsMagnitude || defaultFinancialsMagnitude,
        financialsDecimalCount: financialsDecimalCountToUse,
        hasError: false,
        isLoaded: true,
        errorStatusCode: null,
        skipPowerBiTheme: SkipPowerBiTheme.toLowerCase() === 'true',
        enableTpal: enableTpal.toLowerCase() === 'true',
        enableTableauProject: ENABLE_TABLEAU_PROJECT.toLowerCase() === 'true',
        ENABLE_DYNAMIC_INGESTION: enableDynamicIngestion(),
        StructuredV2Enabled: getFeatureFlagValue(StructuredV2Enabled),
        enablePerformanceTest:
          SharepointPerformanceTestEnabled.toLowerCase() === 'true',
        enablePowerBiIngestion: enablePowerBiIngestion.toLowerCase() === 'true',
        ImageIngestionEnabled,
        showColorConversionModal: isColorConversionModalActive(
          state,
          ContentColourConversionOption
        ),
        settingsApiCount: state.settingsApiCount + 1,
      };
    },
    fetchProjectNavigationSettingsSucceeded: (
      state,
      action: {
        payload: GetProjectNavigationSettingsResponse;
      }
    ) => {
      return {
        ...state,
        navigation: action.payload.nodes,
      };
    },
    fetchProjectDetailsSucceeded: (
      state,
      action: {
        payload: Project;
      }
    ) => {
      return {
        ...state,
        ...action.payload,
        hasError: false,
        errorStatusCode: null,
        isLoaded: true,
      };
    },
    fetchProjectFailed: (
      state,
      action: {
        payload: number;
      }
    ) => ({
      ...state,
      isLoading: false,
      hasError: true,
      status: null,
      errorStatusCode: action.payload,
    }),
    powerBiReportsUpdated: (
      state,
      action: {
        payload: FixMeLaterPowerBIReports[];
      }
    ) => ({
      ...state,
      powerBiReports: action.payload,
    }),
    fetchUserViewCountsSucceeded: (
      state,
      action: {
        payload: { newCount: number; updatedCount: number };
      }
    ) => ({
      ...state,
      ...action.payload,
    }),
    setUsersForCommenting: (
      state,
      action: {
        payload: UserComment[];
      }
    ) => ({
      ...state,
      usersForCommenting: action.payload,
      usersForCommentingLoaded: true,
    }),
  },
});
export default projectSlice.reducer;

// Actions
export const {
  resetProject,
  resetLoadedStatus,
  fetchProjectBegan,
  fetchProjectEnded,
  fetchProjectDetailsSucceeded,
  fetchProjectSettingsSucceeded,
  fetchProjectNavigationSettingsSucceeded,
  fetchProjectFailed,
  powerBiReportsUpdated,
  fetchUserViewCountsSucceeded,
  setUsersForCommenting,
} = projectSlice.actions;

type State = {
  project: Project;
};
// Selectors
export const getProjectLoading = (state: State) => state.project.isLoading;
export const getProjectError = (state: State) => state.project.hasError;
export const getProjectErrorStatusCode = (state: State) =>
  state.project.errorStatusCode;
export const getProject = (state: State) => state.project;
export const getProjectId = (state: State) => state.project.id;
export const getProjectName = (state: State) => state.project.name;
export const getProjectIsLoaded = (state: State) => state.project.isLoaded;
export const getOpenedProjectStatus = (state: State) => state.project.status;
export const getOpenedProjectStatusSafe = (
  state: State
): string | null | undefined => state.project?.status;
export const getProjectSharePointUrl = (state: State) =>
  state.project.sharePointSiteUrl;
export const getEngagementUrl = (state: State) => state.project.EngagementUrl;
export const getWorkspaceId = (state: State) => state.project.WorkspaceId;
export const getDPReportUrl = (state: State) => state.project.DPReportUrl;
export const getDocumentPath = (state: State) => state.project.DocumentPath;
export const getProjectSource = (state: State) => state.project.Source;
export const getProjectTerritory = (state: State) => state.project.territory;
export const getProjectType = (state: State) => state.project.type;
export const getProjectUrl = (state: State) => state.project.SourceProjectUrl;
export const getEngagementId = (state: State) =>
  state.project.SourceEngagementId;
export const getSkipPowerBiReportTheme = (state: State) =>
  state.project.skipPowerBiTheme;
export const getEnableTpal = (state: State) => state.project.enableTpal;
export const getEnablePerformanceTest = (state: State) =>
  state.project.enablePerformanceTest;
export const getEnableTableauProject = (state: State) =>
  state.project.enableTableauProject;
// eslint-disable-next-line @typescript-eslint/no-unsafe-return
export const getPowerBiReports = (state: State) => state.project.powerBiReports;
export const getProjectFinancials = (state: State) => ({
  currency: state.project.currency,
  financialsMagnitude: state.project.financialsMagnitude,
  financialsDecimalCount: state.project.financialsDecimalCount,
});
export const getUsersForCommenting = (state: State) =>
  state.project.usersForCommenting;
export const getUsersForCommentingLoaded = (state: State) =>
  state.project.usersForCommentingLoaded;
export const getTableauFeatureInstanceId = (state: State) =>
  state.project.TableauFeatureInstanceId;
export const isDynamicIngestionEnabled = (state: State) =>
  state.project.ENABLE_DYNAMIC_INGESTION === true;
export const isStructuredV2Enabled = (state: State) =>
  state.project.StructuredV2Enabled === true;
export const isLiveLinkingEnabled = (state: State) =>
  featureFlags.isLiveLinkingEnabled &&
  state.project.DoNotCloneVisualization.toLowerCase() === 'true';
export const getProjectDefaultTheme = (state: State) =>
  state.project.DefaultTheme;
export const getDoNotCloneVisualization = (state: State) =>
  state.project.DoNotCloneVisualization.toLowerCase() === 'true';
export const getManualRefreshEnabled = (state: State) =>
  state.project.ManualRefreshEnabled?.toLowerCase() === 'true';

export const getEnablePowerBiIngestion = (state: State) =>
  state.project.enablePowerBiIngestion === true;

export const getContentColourConversion = (state: State) =>
  state.project.ContentColourConversionOption;
export const getNavigation = (state: State) => state?.project?.navigation;

export const getShowColorConversionModal = (state: State) =>
  state.project.showColorConversionModal;

export const loadProjectThunk = createAsyncThunk(
  'project/get',
  async (projectId: string, thunkApi) => {
    try {
      thunkApi.dispatch(clearAllProjectData());
      thunkApi.dispatch(fetchProjectBegan());
      const projectResponse = await getProjectById(projectId);
      if (projectResponse) {
        thunkApi.dispatch(fetchProjectDetailsSucceeded(projectResponse));
        const [settingsResponse, navigationSettingsResponse] =
          await Promise.all([
            getProjectSettingsById(projectId),
            getProjectNavigationSettings(projectId),
          ]);
        if (settingsResponse) {
          thunkApi.dispatch(fetchProjectSettingsSucceeded(settingsResponse));
        }
        if (navigationSettingsResponse) {
          thunkApi.dispatch(
            fetchProjectNavigationSettingsSucceeded(navigationSettingsResponse)
          );
        }
      }
    } catch (error) {
      const err = error as AxiosError;
      thunkApi.dispatch(
        fetchProjectFailed(err.response ? err.response.status : 500)
      );
    } finally {
      thunkApi.dispatch(fetchProjectEnded());
    }
  },
  {
    condition: (_, { getState }) => {
      const { project } = getState() as { project: Project };
      const { isLoading, isLoaded } = project;

      if (isLoading || isLoaded) {
        return false;
      }

      return true;
    },
  }
);

export const refetchProjectThunk = createAsyncThunk(
  'project/get',
  (projectId: string, thunkApi: { dispatch: Dispatch }) => {
    thunkApi.dispatch(resetLoadedStatus());
    // TODO: FixMeLater. Fix this as it is a new error for me!
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
    const parsedDispatch = thunkApi.dispatch as any;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    parsedDispatch(loadProjectThunk(projectId));
  },
  {
    condition: (_, { getState }) => {
      const { project } = getState() as { project: Project };
      const { isLoading } = project;

      return !isLoading;
    },
  }
);

// Thunks
export const loadPowerBiReportsThunk =
  (projectId: string) => async (dispatch: Dispatch) => {
    // TODO: FixMeLater As this is from powerBiService.js and it is outside of the scope of this POC do it later
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const reports = await getReports(projectId);
    dispatch(powerBiReportsUpdated(reports));
  };

export const updateProjectSettingsThunk =
  (projectId: string, settings: FixMeLaterProjectSettings) =>
  async (dispatch: Dispatch) => {
    try {
      dispatch(fetchProjectBegan());
      await updateProjectSettings(projectId, settings);

      dispatch(fetchProjectSettingsSucceeded(settings));
    } catch (error) {
      // there is an issue with null response on patch request in the http client
      // http client tries to transform response.data and throws an error
      if (error instanceof TypeError) {
        dispatch(fetchProjectSettingsSucceeded(settings));
      } else if (error) {
        const err = error as AxiosError;
        dispatch(fetchProjectFailed(err.response ? err.response.status : 500));
      }
    } finally {
      dispatch(fetchProjectEnded());
    }
  };

export const loadUsersForCommenting =
  (projectId: string) => async (dispatch: Dispatch) => {
    try {
      const response = await getUsersForComments(projectId);
      if (response) {
        dispatch(setUsersForCommenting(response));
      }
    } catch (error) {
      dispatch(setUsersForCommenting([]));
    }
  };
